/**
 * ExperienceHubCore - Shared Experience Hub rendering for Private and Public modes
 * 
 * This component is the single source of truth for:
 * - Explore Hub screen (area selection, 3-column layout, mobile modal)
 * - Preview Route screen (route preview with steps)
 * 
 * Props:
 * - readOnly: boolean - When true, hides edit controls (public mode)
 * - isOwner: boolean - When true AND readOnly=false, shows owner controls
 * 
 * The only differences between private and public:
 * - Edit controls (busyness editor, journey builder, step editor) - hidden when readOnly
 * - Share button - hidden when readOnly
 * - All other UI elements are IDENTICAL
 */

import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  Eye,
  Loader2,
  Share2,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Menu,
  X,
  ImageIcon,
  HelpCircle,
  AlertCircle,
  Check,
  ArrowRight,
  ArrowLeft,
  Clock,
  Bell,
  Home,
  Navigation,
  Coffee,
  DoorOpen,
  Shield,
  Heart,
  Play,
  Plus,
  Edit3,
  Trash2,
  Users,
  Info,
  Layers,
  ChevronUp,
  Map as MapIcon,
  BookOpen,
  Compass
} from 'lucide-react';
import { toast } from 'sonner';
import { buildRouteForDestination } from '../lib/routeBuilder';

// ==================== IMAGE COMPONENTS ====================

const AreaImage = ({ src, alt, className = '' }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  if (!src || imageError) {
    return (
      <div className={`bg-gradient-to-br from-slate-600/50 to-slate-800/50 backdrop-blur-sm flex flex-col items-center justify-center ${className}`}>
        <ImageIcon className="w-10 h-10 text-white/30 mb-2" />
        <span className="text-white/40 text-xs">No image available</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {imageLoading && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-600/50 to-slate-800/50 animate-pulse flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setImageLoading(false)}
        onError={() => {
          setImageError(true);
          setImageLoading(false);
        }}
        loading="lazy"
      />
    </div>
  );
};

const ImageGallery = ({ photos, areaName, className = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  
  const imageUrls = (photos || [])
    .filter(p => p?.url)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(p => p.url);
  
  if (imageUrls.length === 0) {
    return (
      <div className={`bg-gradient-to-br from-slate-600/50 to-slate-800/50 backdrop-blur-sm flex flex-col items-center justify-center ${className}`}>
        <ImageIcon className="w-10 h-10 text-white/30 mb-2" />
        <span className="text-white/40 text-xs">No image available</span>
      </div>
    );
  }
  
  if (imageUrls.length === 1) {
    return <AreaImage src={imageUrls[0]} alt={areaName} className={className} />;
  }

  return (
    <>
      <div className={`relative ${className} cursor-pointer`} onClick={() => setGalleryOpen(true)}>
        <AreaImage src={imageUrls[currentIndex]} alt={areaName} className="w-full h-full" />
        <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
          <ImageIcon className="w-3 h-3 text-white/80" />
          <span className="text-white text-xs font-medium">{currentIndex + 1}/{imageUrls.length}</span>
        </div>
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {imageUrls.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
              className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-white w-4' : 'bg-white/50'}`}
            />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {galleryOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={() => setGalleryOpen(false)}
          >
            <button 
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white z-10"
              onClick={() => setGalleryOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="absolute top-4 left-4 text-white/80 text-sm">
              {currentIndex + 1} / {imageUrls.length}
            </div>
            
            <img 
              src={imageUrls[currentIndex]} 
              alt={`${areaName} - Image ${currentIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            {imageUrls.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(i => Math.max(0, i - 1)); }}
                  disabled={currentIndex === 0}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full disabled:opacity-30"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(i => Math.min(imageUrls.length - 1, i + 1)); }}
                  disabled={currentIndex === imageUrls.length - 1}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full disabled:opacity-30"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {imageUrls.map((url, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
                  className={`w-12 h-12 rounded overflow-hidden border-2 transition-all ${
                    i === currentIndex ? 'border-white' : 'border-transparent opacity-60'
                  }`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ==================== HELPER FUNCTIONS ====================

const getTrafficLightColor = (trafficLight) => {
  switch (trafficLight) {
    case 'green': return '#22c55e';
    case 'amber': return '#f59e0b';
    case 'red': return '#ef4444';
    default: return '#94a3b8';
  }
};

const getTrafficLightLabel = (trafficLight) => {
  switch (trafficLight) {
    case 'green': return 'Low';
    case 'amber': return 'Moderate';
    case 'red': return 'High';
    default: return 'Not rated';
  }
};

const getAreaSummary = (categories) => {
  if (!categories || categories.length === 0) return null;
  
  let totalScore = 0;
  categories.forEach(cat => {
    const score = cat.traffic_light === 'green' ? 2 : cat.traffic_light === 'amber' ? 3 : 4;
    totalScore += score;
  });
  
  const avgScore = totalScore / categories.length;
  
  if (avgScore <= 2.3) {
    return {
      label: 'Low sensory environment',
      summary: 'This area is generally calm and easy to navigate.',
      tip: 'A good choice if you prefer quieter spaces.'
    };
  } else if (avgScore <= 3.3) {
    return {
      label: 'Moderate sensory environment',
      summary: 'This area may feel busy at times but is usually manageable.',
      tip: 'Best visited during quieter periods if sensitive to stimulation.'
    };
  } else {
    return {
      label: 'Higher sensory environment',
      summary: 'This area may feel overwhelming, especially during peak times.',
      tip: 'Consider visiting during off-peak hours or prepare for stimulation.'
    };
  }
};

// ==================== TRANSITION TYPES CONFIG ====================

const TRANSITION_TYPES = {
  corridor: { label: 'Through corridor', icon: ArrowRight, duration: '15-20 sec' },
  corridor_short: { label: 'Short corridor', icon: ArrowRight, duration: '5-10 sec' },
  corridor_long: { label: 'Long corridor', icon: ArrowRight, duration: '30-45 sec' },
  open_space: { label: 'Open space', icon: Layers, duration: '10-15 sec' },
  doors: { label: 'Through doors', icon: DoorOpen, duration: '5 sec' },
  stairs: { label: 'Stairs', icon: ChevronUp, duration: '15-20 sec' },
  ramp: { label: 'Ramp', icon: ArrowRight, duration: '10-15 sec' },
  narrow: { label: 'Narrow passage', icon: ArrowRight, duration: '5-10 sec' },
  default: { label: 'Moving between', icon: ArrowRight, duration: '10-15 sec' },
  custom: { label: 'Custom step', icon: ArrowRight, duration: '10-15 sec' }
};

// ==================== MAIN COMPONENT ====================

const ExperienceHubCore = ({
  // Mode props
  readOnly = false,
  isOwner = false,
  
  // Data props
  allAreasWithData = [],
  sensorySummary = {},
  venue = {},
  venueStatus = null,
  busynessNotes = '',
  movementSteps = [],
  submission = null,
  submissionId = '',
  
  // State props (lifted from parent)
  selectedAreaId,
  setSelectedAreaId,
  selectedArea,
  setSelectedArea,
  mobileDetailOpen,
  setMobileDetailOpen,
  currentScreen,
  setCurrentScreen,
  routeTargetArea,
  setRouteTargetArea,
  currentStepIndex,
  setCurrentStepIndex,
  showVenueAbout,
  setShowVenueAbout,
  menuOpen,
  setMenuOpen,
  
  // Journey Builder state (private only)
  journeyEditMode = false,
  setJourneyEditMode,
  
  // Callbacks
  onEditBusyness,
  onViewSensoryDetails,
  onAddMovementStep,
  onEditMovementStep,
  onDeleteMovementStep,
  onShareGuide,
  
  // Computed data from parent
  bestTimeToVisit = null,
  busynessLastUpdated = null
}) => {
  
  // ==================== COMPUTED VALUES ====================
  
  const venueName = venue?.name || 'Venue';
  const venueDescription = venue?.description || '';
  const venueAddress = venue?.address || '';
  const logoUrl = venue?.logo_url || null;
  
  // Selected area data
  const selectedAreaData = useMemo(() => {
    if (!selectedAreaId) return allAreasWithData[0] || null;
    return allAreasWithData.find(a => a.area_id === selectedAreaId) || allAreasWithData[0] || null;
  }, [selectedAreaId, allAreasWithData]);
  
  // ==================== ROUTE GENERATION ====================
  // Uses shared routeBuilder utility for single source of truth
  
  // Get movement text for step - simplified, no auto-generated transitions
  const getMovementText = useCallback((step, nextStep, isTransition = false) => {
    const name = step?.area_name || step?.name || 'this area';
    const nextName = nextStep?.area_name || nextStep?.name;
    
    // For custom movement steps only
    if (isTransition && step?.isCustom) {
      return {
        primary: step.transitionDescription || 'Moving between spaces',
        secondary: null,
        progression: nextName ? `Next: ${step.transitionDuration || '10-15 sec'} to ${nextName}` : null
      };
    }
    
    let primary = '';
    if (step?.stepType === 'start') {
      primary = `You arrive at ${name}.`;
    } else if (step?.stepType === 'destination') {
      primary = `You reach ${name}.`;
    } else {
      primary = `You pass through ${name}.`;
    }
    
    let secondary = '';
    const categories = step?.categories || [];
    const crowdCat = categories.find(c => (c.category || '').toLowerCase().includes('crowd'));
    const noiseCat = categories.find(c => (c.category || '').toLowerCase().includes('noise'));
    
    if (crowdCat?.traffic_light === 'red' && noiseCat?.traffic_light === 'red') {
      secondary = 'Can feel busy and loud.';
    } else if (crowdCat?.traffic_light === 'red' || crowdCat?.traffic_light === 'amber') {
      secondary = 'May feel busy at times.';
    } else if (noiseCat?.traffic_light === 'red' || noiseCat?.traffic_light === 'amber') {
      secondary = 'Can be moderately noisy.';
    } else {
      secondary = 'Generally calm and manageable.';
    }
    
    let progression = null;
    if (nextStep && !nextStep.isTransition) {
      const nextStepName = nextStep.area_name || nextStep.name;
      progression = `Next: ${nextStepName}`;
    }
    
    return { primary, secondary, progression };
  }, []);
  
  // Get quiet exit options
  const getExitOptions = useCallback(() => {
    return allAreasWithData
      .filter(a => a.trafficLight === 'green' && (a.area_name || a.name || '').toLowerCase().includes('exit'))
      .slice(0, 2);
  }, [allAreasWithData]);
  
  // Current route data - uses shared routeBuilder utility
  const customRoutes = submission?.custom_routes || [];
  const route = useMemo(() => {
    if (!routeTargetArea) return [];
    return buildRouteForDestination({
      allAreasWithData,
      movementSteps,
      customRoutes,
      targetArea: routeTargetArea
    });
  }, [routeTargetArea, allAreasWithData, movementSteps, customRoutes]);
  
  const currentStep = route[currentStepIndex] || null;
  const nextStep = route[currentStepIndex + 1] || null;
  const areaSteps = route.filter(s => !s.isTransition);
  const currentAreaIndex = areaSteps.findIndex(s => s.area_id === currentStep?.area_id);
  const movementText = currentStep ? getMovementText(currentStep, nextStep, currentStep.isTransition) : null;
  const exitOptions = getExitOptions();
  
  // ==================== HANDLERS ====================
  
  const handleAreaSelect = (area) => {
    setSelectedAreaId(area.area_id);
    setSelectedArea(area);
    setMobileDetailOpen(true);
  };
  
  const handlePreviewRoute = (area) => {
    setRouteTargetArea(area);
    setCurrentStepIndex(0);
    setCurrentScreen('preview_route');
  };
  
  const goToExploreHub = () => {
    setCurrentScreen('explore_hub');
  };

  // ==================== RENDER ====================
  
  return (
    <>
      {/* ==================== EXPLORE HUB SCREEN ==================== */}
      {currentScreen === 'explore_hub' && (
        <motion.div
          key="explore-hub"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="h-full pb-20"
        >
          {/* HEADER */}
          <div className="bg-[#1a365d] -mx-6 -mt-4 px-4 py-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-7 object-contain" />
              ) : (
                <span className="text-white font-bold text-lg tracking-wide">nysa</span>
              )}
              <span className="text-white/90 text-sm font-medium hidden sm:block">{venueName}</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Share Button - private only */}
              {!readOnly && onShareGuide && (
                <button 
                  onClick={onShareGuide}
                  className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                  data-testid="share-guide-btn"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  <span>Share</span>
                </button>
              )}
              <button 
                onClick={() => setMenuOpen(true)}
                className="p-2 text-white/70 hover:text-white"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* REAL-TIME STATUS SECTION */}
          {venueStatus && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-4"
              data-testid="venue-status-section"
            >
              <div 
                className="backdrop-blur-xl rounded-2xl p-5 border"
                style={{ 
                  backgroundColor: venueStatus.colorLight,
                  borderColor: `${venueStatus.color}30`
                }}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${venueStatus.color}25` }}
                    >
                      {venueStatus.status === 'calm' && <Shield className="w-6 h-6" style={{ color: venueStatus.color }} />}
                      {venueStatus.status === 'moderate' && <Clock className="w-6 h-6" style={{ color: venueStatus.color }} />}
                      {venueStatus.status === 'busy' && <Users className="w-6 h-6" style={{ color: venueStatus.color }} />}
                      {venueStatus.status === 'intense' && <AlertCircle className="w-6 h-6" style={{ color: venueStatus.color }} />}
                      {(venueStatus.status === 'not_set' || venueStatus.status === 'unknown') && <HelpCircle className="w-6 h-6" style={{ color: venueStatus.color }} />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-lg font-bold" style={{ color: venueStatus.color }}>
                          {venueStatus.label}
                        </span>
                        {venueStatus.hasData && <span className="text-white/60 text-sm">right now</span>}
                      </div>
                      <p className="text-white/80 text-sm leading-relaxed" data-testid="venue-status-summary">
                        {venueStatus.summary}
                      </p>
                    </div>
                  </div>
                  
                  {/* Edit button - private owner only */}
                  {!readOnly && isOwner && onEditBusyness && (
                    <button
                      onClick={onEditBusyness}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors"
                      data-testid="edit-busyness-btn"
                      title="Edit visitor levels"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {(bestTimeToVisit || busynessLastUpdated) && (
                  <>
                    <div className="h-px bg-white/10 my-3" />
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                      {bestTimeToVisit && (
                        <div className="flex items-start gap-2 flex-1">
                          <Clock className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-white/50 text-xs uppercase tracking-wider block mb-0.5">Best time to visit</span>
                            <span className="text-white/80 text-sm" data-testid="venue-best-time">{bestTimeToVisit}</span>
                          </div>
                        </div>
                      )}
                      
                      {venueStatus.reassurance && (
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div 
                            className="flex items-center gap-2 px-3 py-2 rounded-xl"
                            style={{ backgroundColor: `${venueStatus.color}15` }}
                          >
                            <Heart className="w-4 h-4 flex-shrink-0" style={{ color: venueStatus.color }} />
                            <span 
                              className="text-sm font-medium"
                              style={{ color: venueStatus.color }}
                              data-testid="venue-reassurance"
                            >
                              {venueStatus.reassurance}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {busynessLastUpdated && (
                      <div className="mt-3 text-white/40 text-xs flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        <span data-testid="busyness-updated">{busynessLastUpdated}</span>
                      </div>
                    )}
                  </>
                )}
                
                {busynessNotes && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-white/60 text-xs italic">"{busynessNotes}"</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* VENUE INFO SECTION */}
          <div 
            className="backdrop-blur-xl rounded-2xl p-5 mb-6 border border-white/10"
            style={{ backgroundColor: '#1e3a5fe6' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white mb-1" data-testid="venue-name">{venueName}</h1>
                
                {venueAddress && (
                  <div className="flex items-center gap-2 text-white/70 text-sm mb-2" data-testid="venue-address">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{venueAddress}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getTrafficLightColor(sensorySummary.overall?.traffic_light) }}
                    />
                    <span className="text-white/90 text-sm font-medium">
                      {getTrafficLightLabel(sensorySummary.overall?.traffic_light)} sensory environment overall
                    </span>
                  </div>
                  <span className="text-white/50 text-xs">
                    {allAreasWithData.length} areas
                  </span>
                </div>
              </div>
              
              <button
                onClick={() => {
                  const firstArea = allAreasWithData[0];
                  if (firstArea) handleAreaSelect(firstArea);
                }}
                className="flex-shrink-0 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors flex items-center gap-2"
                data-testid="explore-venue-btn"
              >
                <MapPin className="w-4 h-4" />
                Explore the venue
              </button>
            </div>
            
            {venueDescription && venueDescription.length > 50 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <span className="text-white/60 text-xs uppercase tracking-wider font-medium block mb-2">About this venue</span>
                <p className="text-white/70 text-sm leading-relaxed">
                  {venueDescription}
                </p>
              </div>
            )}
            
            {/* Venue Context - Collapsible dropdown */}
            {(venue?.venue_type || venue?.venue_size || venue?.structural_characteristics?.length > 0 || venue?.entry_models?.length > 0) && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <button
                  onClick={() => setShowVenueAbout(!showVenueAbout)}
                  className="flex items-center justify-between w-full text-left group"
                >
                  <span className="text-white/60 text-xs uppercase tracking-wider font-medium">Venue Context</span>
                  <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${showVenueAbout ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {showVenueAbout && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 mt-3">
                        {venue.venue_type && (
                          <div className="flex items-start gap-2">
                            <span className="text-white/50 text-xs min-w-[80px]">Type:</span>
                            <span className="text-white/80 text-sm">{venue.venue_type}</span>
                          </div>
                        )}
                        
                        {venue.venue_size && (
                          <div className="flex items-start gap-2">
                            <span className="text-white/50 text-xs min-w-[80px]">Size:</span>
                            <span className="text-white/80 text-sm">{venue.venue_size}</span>
                          </div>
                        )}
                        
                        {venue.entry_models?.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-white/50 text-xs min-w-[80px]">Entry:</span>
                            <span className="text-white/80 text-sm">{venue.entry_models.join(', ')}</span>
                          </div>
                        )}
                        
                        {venue.structural_characteristics?.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-white/50 text-xs min-w-[80px]">Layout:</span>
                            <span className="text-white/80 text-sm">{venue.structural_characteristics.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Venue Resources row (v1: simple URL links only).
              Shown only when at least one URL is present. Missing URLs render as
              greyed-out, disabled buttons; present URLs open in a new tab. */}
          {(venue?.map_url || venue?.guide_url || venue?.virtual_tour_url) && (
            <div
              className="mb-4 flex flex-wrap gap-3"
              data-testid="venue-resources-row"
            >
              {[
                { url: venue?.map_url,          label: 'Map',              Icon: MapIcon,  testId: 'venue-resource-map-button' },
                { url: venue?.guide_url,        label: 'Guide',            Icon: BookOpen, testId: 'venue-resource-guide-button' },
                { url: venue?.virtual_tour_url, label: 'Virtual 360 Tour', Icon: Compass,  testId: 'venue-resource-virtual-tour-button' },
              ].map(({ url, label, Icon, testId }) => {
                const active = !!(url && url.trim());
                const commonClasses =
                  'inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-white/10 backdrop-blur-xl transition-colors';
                if (active) {
                  return (
                    <a
                      key={testId}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${commonClasses} text-white hover:bg-white/10`}
                      style={{ backgroundColor: '#1e3a5fe6' }}
                      data-testid={testId}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </a>
                  );
                }
                return (
                  <button
                    key={testId}
                    type="button"
                    disabled
                    aria-disabled="true"
                    className={`${commonClasses} text-white/40 cursor-not-allowed`}
                    style={{ backgroundColor: 'rgba(30,58,95,0.4)' }}
                    data-testid={testId}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* 3-COLUMN LAYOUT */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* LEFT PANEL - Area Navigation List */}
            <div className="lg:w-64 flex-shrink-0">
              <div 
                className="backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10"
                style={{ backgroundColor: '#1e3a5fe6' }}
              >
                <div className="px-4 py-3 border-b border-white/10">
                  <h2 className="text-white/90 text-xs font-semibold uppercase tracking-wider">Experience Hub</h2>
                </div>
                <div className="max-h-[50vh] lg:max-h-[60vh] overflow-y-auto">
                  {allAreasWithData.map((area, index) => (
                    <button
                      key={area.area_id || index}
                      onClick={() => handleAreaSelect(area)}
                      data-testid={`hub-area-nav-${area.area_id}`}
                      className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-all border-b border-white/5 last:border-0 ${
                        selectedAreaId === area.area_id 
                          ? 'bg-white/15' 
                          : 'hover:bg-white/10'
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        selectedAreaId === area.area_id ? 'bg-white/20' : ''
                      }`}>
                        {selectedAreaId === area.area_id ? (
                          <ChevronRight className="w-3 h-3 text-white" />
                        ) : (
                          <Check className="w-3 h-3 text-white/50" />
                        )}
                      </span>
                      <span className="flex-1 text-white text-sm truncate">{area.area_name || area.name}</span>
                      <span 
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{ 
                          backgroundColor: `${getTrafficLightColor(area.trafficLight)}20`,
                          color: getTrafficLightColor(area.trafficLight)
                        }}
                      >
                        {area.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* MIDDLE PANEL - Area Cards Grid */}
            <div className="flex-1 min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allAreasWithData.map((area, index) => {
                  const areaSummary = getAreaSummary(area.categories);
                  const shortSummary = areaSummary?.summary?.split('.')[0] || 'Tap to explore';
                  
                  return (
                    <motion.div
                      key={area.area_id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleAreaSelect(area)}
                      data-testid={`hub-area-card-${area.area_id}`}
                      className={`bg-white/10 backdrop-blur-xl rounded-2xl overflow-hidden cursor-pointer transition-all hover:bg-white/15 border-2 ${
                        selectedAreaId === area.area_id 
                          ? 'border-white/40 ring-2 ring-white/20' 
                          : 'border-transparent'
                      }`}
                    >
                      <AreaImage 
                        src={area.image} 
                        alt={area.area_name || area.name}
                        className="aspect-[16/10]"
                      />
                      
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-semibold text-sm truncate flex-1">{area.area_name || area.name}</span>
                          <span 
                            className="px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ml-2"
                            style={{ 
                              backgroundColor: getTrafficLightColor(area.trafficLight),
                              color: 'white'
                            }}
                          >
                            {area.label}
                          </span>
                        </div>
                        
                        <p className="text-white/60 text-xs line-clamp-1">
                          {shortSummary}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT PANEL - Selected Area Detail (Desktop) */}
            <div className="hidden lg:block lg:w-80 flex-shrink-0">
              {selectedAreaData && (
                <div 
                  className="backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 sticky top-4"
                  style={{ backgroundColor: '#1e3a5fe6' }}
                >
                  <div className="bg-[#1a365d] px-4 py-3 flex items-center justify-between">
                    <span className="text-white font-semibold">{selectedAreaData.area_name || selectedAreaData.name}</span>
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-bold"
                      style={{ 
                        backgroundColor: getTrafficLightColor(selectedAreaData.trafficLight),
                        color: 'white'
                      }}
                    >
                      {selectedAreaData.label}
                    </span>
                  </div>
                  
                  <ImageGallery 
                    photos={selectedAreaData.photos} 
                    areaName={selectedAreaData.area_name || selectedAreaData.name}
                    className="aspect-video"
                  />
                  
                  <div className="p-4 space-y-4">
                    <div className="bg-white/5 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getTrafficLightColor(selectedAreaData.trafficLight) }}
                        />
                        <span className="text-white font-semibold text-sm">
                          {selectedAreaData.label} sensory environment
                        </span>
                      </div>
                      <p className="text-white/70 text-sm">
                        {getAreaSummary(selectedAreaData.categories)?.summary || 'Generally manageable for most visitors.'}
                      </p>
                    </div>
                    
                    {selectedAreaData.description && (
                      <div>
                        <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2">About this area</h4>
                        <p className="text-white/80 text-sm leading-relaxed">
                          {selectedAreaData.description}
                        </p>
                      </div>
                    )}
                    
                    {/* Action Buttons - View Sensory Details ALWAYS visible */}
                    <div className="space-y-2">
                      {onViewSensoryDetails && (
                        <button 
                          onClick={() => onViewSensoryDetails(selectedAreaData)}
                          className="w-full bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                          data-testid="view-sensory-details-btn"
                        >
                          <Eye className="w-4 h-4" />
                          View Sensory Details
                        </button>
                      )}
                      <button 
                        onClick={() => handlePreviewRoute(selectedAreaData)}
                        data-testid="preview-route-btn"
                        className="w-full text-white text-sm py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                        style={{ backgroundColor: '#22c55e' }}
                      >
                        <Navigation className="w-4 h-4" />
                        PREVIEW YOUR ROUTE
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* MOBILE DETAIL PANEL - Slide up sheet */}
          <AnimatePresence>
            {mobileDetailOpen && selectedAreaData && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
                onClick={() => setMobileDetailOpen(false)}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25 }}
                  className="absolute bottom-0 left-0 right-0 rounded-t-3xl max-h-[85vh] overflow-y-auto"
                  style={{ backgroundColor: '#1e3a5f' }}
                  onClick={e => e.stopPropagation()}
                >
                  <div className="flex justify-center py-3">
                    <div className="w-10 h-1 bg-white/30 rounded-full" />
                  </div>
                  
                  <div className="px-4 pb-3 flex items-center justify-between border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">{selectedAreaData.area_name || selectedAreaData.name}</span>
                      <span 
                        className="px-2 py-0.5 rounded text-xs font-bold"
                        style={{ 
                          backgroundColor: getTrafficLightColor(selectedAreaData.trafficLight),
                          color: 'white'
                        }}
                      >
                        {selectedAreaData.label}
                      </span>
                    </div>
                    <button onClick={() => setMobileDetailOpen(false)} className="p-2">
                      <X className="w-5 h-5 text-white/70" />
                    </button>
                  </div>
                  
                  <ImageGallery 
                    photos={selectedAreaData.photos} 
                    areaName={selectedAreaData.area_name || selectedAreaData.name}
                    className="aspect-video"
                  />
                  
                  <div className="p-4 space-y-4">
                    <div className="bg-white/5 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getTrafficLightColor(selectedAreaData.trafficLight) }}
                        />
                        <span className="text-white font-semibold text-sm">
                          {selectedAreaData.label} sensory environment
                        </span>
                      </div>
                      <p className="text-white/70 text-sm">
                        {getAreaSummary(selectedAreaData.categories)?.summary || 'Generally manageable for most visitors.'}
                      </p>
                    </div>
                    
                    {selectedAreaData.description && (
                      <div>
                        <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2">About this area</h4>
                        <p className="text-white/80 text-sm leading-relaxed">
                          {selectedAreaData.description}
                        </p>
                      </div>
                    )}
                    
                    {/* Action Buttons - View Sensory Details ALWAYS visible */}
                    <div className="space-y-2 pt-2">
                      {onViewSensoryDetails && (
                        <button 
                          onClick={() => { setMobileDetailOpen(false); onViewSensoryDetails(selectedAreaData); }}
                          className="w-full bg-white/10 hover:bg-white/20 text-white text-sm py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                          data-testid="mobile-view-sensory-details-btn"
                        >
                          <Eye className="w-4 h-4" />
                          View Sensory Details
                        </button>
                      )}
                      <button 
                        onClick={() => { setMobileDetailOpen(false); handlePreviewRoute(selectedAreaData); }}
                        data-testid="mobile-preview-route-btn"
                        className="w-full text-white text-sm py-3.5 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                        style={{ backgroundColor: '#22c55e' }}
                      >
                        <Navigation className="w-4 h-4" />
                        PREVIEW YOUR ROUTE
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* FOOTER BAR */}
          <div className="fixed bottom-0 left-0 right-0 bg-[#1a365d]/95 backdrop-blur-xl border-t border-white/10 px-4 py-3 safe-area-bottom z-40">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div className="hidden sm:flex items-center gap-4 text-white/60 text-xs">
                <a href="#" className="hover:text-white flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Location
                </a>
                <a href="#" className="hover:text-white flex items-center gap-1">
                  <Info className="w-3 h-3" /> Help
                </a>
              </div>
              <button 
                onClick={() => selectedAreaData && handlePreviewRoute(selectedAreaData)}
                disabled={!selectedAreaData}
                className="flex-1 sm:flex-none text-white py-3 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#22c55e' }}
                data-testid="footer-preview-visit-btn"
              >
                <Play className="w-4 h-4" />
                PREVIEW YOUR VISIT
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* ==================== PREVIEW ROUTE SCREEN ==================== */}
      {currentScreen === 'preview_route' && routeTargetArea && (
        <motion.div
          key="preview-route"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="h-full pb-4"
        >
          {/* ROUTE HEADER */}
          <div className="bg-[#1a365d] -mx-6 -mt-4 px-4 py-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-7 object-contain" />
              ) : (
                <span className="text-white font-bold text-lg tracking-wide">nysa</span>
              )}
              <span className="text-white/90 text-sm font-medium">Preview Your Visit</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Journey Edit Mode Toggle - private only */}
              {!readOnly && setJourneyEditMode && (
                <button 
                  onClick={() => setJourneyEditMode(!journeyEditMode)}
                  data-testid="journey-edit-toggle"
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                    journeyEditMode 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  <span>{journeyEditMode ? 'Editing' : 'Build Journey'}</span>
                </button>
              )}
              <button 
                onClick={goToExploreHub}
                className="p-2 text-white/70 hover:text-white flex items-center gap-1 text-sm"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Home</span>
              </button>
              <button 
                onClick={() => setMenuOpen(true)}
                className="p-2 text-white/70 hover:text-white"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-4 pb-20">
            {/* PROGRESS BAR */}
            <div 
              className="backdrop-blur-xl rounded-xl p-4 border border-white/10"
              style={{ backgroundColor: '#1e3a5fe6' }}
            >
              <div className="flex items-center justify-between gap-2 overflow-x-auto">
                {areaSteps.map((step, idx) => {
                  const isCompleted = route.indexOf(step) < currentStepIndex;
                  const isCurrent = step.area_id === currentStep?.area_id || 
                                  (currentStep?.isTransition && idx === currentAreaIndex);
                  return (
                    <React.Fragment key={step.area_id}>
                      <button
                        onClick={() => setCurrentStepIndex(route.indexOf(step))}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                          isCurrent
                            ? 'bg-white text-[#1a365d]'
                            : isCompleted
                              ? 'bg-emerald-500/30 text-emerald-300'
                              : 'bg-white/10 text-white/60 hover:bg-white/20'
                        }`}
                      >
                        {isCompleted && <Check className="w-3 h-3" />}
                        <span className="truncate max-w-[80px]">{step.area_name || step.name}</span>
                      </button>
                      {idx < areaSteps.length - 1 && (
                        <ArrowRight className="w-4 h-4 text-white/30 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-4">
              {/* LEFT COLUMN - Route Steps List */}
              <div className="lg:w-72 flex-shrink-0">
                <div 
                  className="backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10"
                  style={{ backgroundColor: '#1e3a5fe6' }}
                >
                  <div className="px-4 py-3 border-b border-white/10">
                    <h2 className="text-white/90 text-xs font-semibold uppercase tracking-wider">
                      Your Route to {routeTargetArea.area_name || routeTargetArea.name}
                    </h2>
                    <p className="text-white/50 text-xs mt-1">
                      {journeyEditMode 
                        ? 'Tap + to add custom movement steps' 
                        : 'Typical journey through the venue'}
                    </p>
                  </div>
                  
                  {/* Edit Mode Banner - private only */}
                  {!readOnly && journeyEditMode && (
                    <div className="px-4 py-2 bg-amber-500/20 border-b border-amber-500/30 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Edit3 className="w-4 h-4 text-amber-400" />
                        <span className="text-amber-300 text-xs font-medium">Journey Edit Mode</span>
                      </div>
                      <button
                        onClick={() => setJourneyEditMode(false)}
                        className="text-amber-400 text-xs hover:text-amber-300 underline"
                      >
                        Done
                      </button>
                    </div>
                  )}
                  
                  {/* Route Steps */}
                  <div className="max-h-[50vh] overflow-y-auto">
                    {route.map((step, index) => {
                      const isTransition = step.isTransition;
                      const isCustomStep = step.isCustom;
                      const TransIcon = isTransition ? (TRANSITION_TYPES[step.transitionType]?.icon || ArrowRight) : null;
                      
                      const showAddButton = !readOnly && journeyEditMode && !isTransition && index < route.length - 1;
                      const nextStepItem = route[index + 1];
                      const nextAreaStep = nextStepItem?.isTransition ? route[index + 2] : nextStepItem;
                      
                      return (
                        <React.Fragment key={step.area_id || index}>
                          <div
                            className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-all border-b border-white/5 ${
                              currentStepIndex === index 
                                ? isTransition ? 'bg-amber-500/10' : 'bg-white/15' 
                                : isTransition ? 'bg-white/5 hover:bg-white/10' : 'hover:bg-white/10'
                            }`}
                          >
                            <button
                              onClick={() => setCurrentStepIndex(index)}
                              data-testid={`route-step-${index}`}
                              className="flex items-center gap-3 flex-1 min-w-0"
                            >
                              {isTransition ? (
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isCustomStep ? 'bg-emerald-500/30' : 'bg-amber-500/20'
                                }`}>
                                  {isCustomStep ? (
                                    <Edit3 className="w-3 h-3 text-emerald-400" />
                                  ) : (
                                    <TransIcon className="w-3 h-3 text-amber-400" />
                                  )}
                                </span>
                              ) : (
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                                  index < currentStepIndex 
                                    ? 'bg-emerald-500 text-white'
                                    : currentStepIndex === index
                                      ? 'bg-white text-[#1a365d]'
                                      : 'bg-white/20 text-white/70'
                                }`}>
                                  {index < currentStepIndex ? <Check className="w-3 h-3" /> : areaSteps.indexOf(step) + 1}
                                </span>
                              )}
                              
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm truncate block ${
                                  isTransition 
                                    ? isCustomStep ? 'text-emerald-300 font-medium' : 'text-amber-300/80 italic'
                                    : 'text-white'
                                }`}>
                                  {isTransition ? step.transitionLabel : (step.area_name || step.name)}
                                </span>
                                <span className="text-white/50 text-xs">
                                  {isTransition ? (
                                    <span className={isCustomStep ? 'text-emerald-400/70' : 'text-amber-400/70'}>
                                      {isCustomStep ? 'Custom step' : step.transitionDuration}
                                    </span>
                                  ) : currentStepIndex === index ? (
                                    <span className="text-emerald-400 font-medium">You are here</span>
                                  ) : index === currentStepIndex + 1 ? (
                                    <span className="text-amber-400">Next</span>
                                  ) : step.stepType === 'start' ? 'Start' : 
                                   step.stepType === 'destination' ? 'Destination' : 
                                   'Stop'}
                                </span>
                              </div>
                            </button>
                            
                            {/* Edit/Delete buttons for custom steps - private only */}
                            {!readOnly && journeyEditMode && isTransition && isCustomStep && (
                              <div className="flex items-center gap-1">
                                {onEditMovementStep && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEditMovementStep(step.customStepId);
                                    }}
                                    data-testid={`edit-step-${index}`}
                                    className="p-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 transition-colors"
                                  >
                                    <Edit3 className="w-3 h-3" />
                                  </button>
                                )}
                                {onDeleteMovementStep && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteMovementStep(step.customStepId);
                                    }}
                                    data-testid={`delete-step-${index}`}
                                    className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* ADD MOVEMENT STEP BUTTON - private only */}
                          {showAddButton && onAddMovementStep && (
                            <button
                              onClick={() => {
                                onAddMovementStep(
                                  step.area_id,
                                  nextAreaStep?.area_id,
                                  step.area_name || step.name,
                                  nextAreaStep?.area_name || nextAreaStep?.name
                                );
                              }}
                              data-testid={`add-step-after-${index}`}
                              className="w-full py-2 flex items-center justify-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 border-y border-amber-500/20 text-amber-400 text-xs font-medium transition-all group"
                            >
                              <Plus className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                              <span>Add movement step</span>
                            </button>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                  
                  {/* Exit Options */}
                  {exitOptions.length > 0 && (
                    <>
                      <div className="px-4 py-2 border-t border-white/10 bg-white/5">
                        <h3 className="text-white/60 text-xs uppercase tracking-wider">Quiet Exits</h3>
                      </div>
                      <div>
                        {exitOptions.map((exit, i) => (
                          <div key={exit.area_id || i} className="px-4 py-2 flex items-center gap-2 text-white/70 text-sm border-b border-white/5 last:border-0">
                            <DoorOpen className="w-4 h-4 text-emerald-400" />
                            <span className="truncate">{exit.area_name || exit.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                
                {/* Confidence Badge */}
                <div className="mt-3 bg-emerald-500/20 border border-emerald-500/30 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-5 h-5 text-emerald-400" />
                    <span className="text-emerald-300 text-sm font-semibold">You can do this</span>
                  </div>
                  <p className="text-emerald-300/70 text-xs pl-7">
                    Each step is manageable. Take your time.
                  </p>
                </div>
              </div>
              
              {/* MIDDLE COLUMN - Visual Flow (Desktop) */}
              <div className="hidden lg:flex flex-1 items-center justify-center overflow-x-auto py-4">
                <div className="flex items-center gap-2">
                  {areaSteps.map((step, index) => {
                    const fullIndex = route.indexOf(step);
                    const isCompleted = fullIndex < currentStepIndex;
                    const isCurrent = fullIndex === currentStepIndex || 
                                    (route[currentStepIndex]?.isTransition && index === Math.floor(currentStepIndex / 2));
                    
                    return (
                      <React.Fragment key={step.area_id}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.1 }}
                          onClick={() => setCurrentStepIndex(fullIndex)}
                          className={`relative bg-white/10 backdrop-blur-xl rounded-xl overflow-hidden cursor-pointer transition-all w-32 flex-shrink-0 ${
                            isCurrent ? 'ring-2 ring-white/50 scale-105' : isCompleted ? 'opacity-70' : ''
                          }`}
                        >
                          <AreaImage 
                            src={step.image} 
                            alt={step.area_name || step.name}
                            className="aspect-square"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-2">
                            <span className="text-white text-xs font-medium block truncate">{step.area_name || step.name}</span>
                          </div>
                          <div className={`absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            isCompleted 
                              ? 'bg-emerald-500 text-white'
                              : isCurrent
                                ? 'bg-white text-[#1a365d]'
                                : 'bg-white/30 text-white'
                          }`}>
                            {isCompleted ? <Check className="w-3 h-3" /> : index + 1}
                          </div>
                        </motion.div>
                        
                        {index < areaSteps.length - 1 && (
                          <div className="flex flex-col items-center px-1 flex-shrink-0">
                            <div className="text-white/40 text-[10px] mb-1">→</div>
                            <ArrowRight className="w-5 h-5 text-white/30" />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              
              {/* RIGHT COLUMN - Current Step Detail */}
              <div className="lg:w-80 flex-shrink-0">
                <div 
                  className={`backdrop-blur-xl rounded-2xl overflow-hidden border ${
                    currentStep?.isTransition 
                      ? currentStep?.isCustom
                        ? 'bg-emerald-900/30 border-emerald-500/30'
                        : 'bg-amber-900/30 border-amber-500/30' 
                      : 'border-white/10'
                  }`}
                  style={!currentStep?.isTransition ? { backgroundColor: '#1e3a5fe6' } : {}}
                >
                  <div className={`px-4 py-3 ${
                    currentStep?.isTransition 
                      ? currentStep?.isCustom
                        ? 'bg-emerald-900/50'
                        : 'bg-amber-900/50'
                      : 'bg-[#1a365d]'
                  }`}>
                    {currentStep?.isTransition ? (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {currentStep?.isCustom ? (
                              <Edit3 className="w-4 h-4 text-emerald-400" />
                            ) : (
                              <ArrowRight className="w-4 h-4 text-amber-400" />
                            )}
                            <span className={`text-xs uppercase tracking-wider ${
                              currentStep?.isCustom ? 'text-emerald-300' : 'text-amber-300'
                            }`}>
                              {currentStep?.isCustom ? 'Custom Movement Step' : 'Moving between spaces'}
                            </span>
                          </div>
                          {/* Edit button for current custom step - private only */}
                          {!readOnly && journeyEditMode && currentStep?.isCustom && onEditMovementStep && (
                            <button
                              onClick={() => onEditMovementStep(currentStep.customStepId)}
                              className="p-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 transition-colors"
                              data-testid="edit-current-step-btn"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <h2 className="text-white font-semibold">{currentStep.transitionLabel}</h2>
                      </>
                    ) : (
                      <>
                        <h2 className="text-white font-semibold text-lg">{currentStep?.area_name || currentStep?.name}</h2>
                        {movementText?.progression && (
                          <p className="text-emerald-400 text-sm mt-1">{movementText.progression}</p>
                        )}
                      </>
                    )}
                  </div>
                  
                  {!currentStep?.isTransition && (
                    <AreaImage 
                      src={currentStep?.image} 
                      alt={currentStep?.area_name || currentStep?.name}
                      className="aspect-video"
                    />
                  )}
                  
                  <div className="p-4 space-y-3">
                    {currentStep?.isTransition && currentStep?.isCustom ? (
                      <>
                        {currentStep.transitionDescription && (
                          <p className="text-white font-medium">
                            {currentStep.transitionDescription}
                          </p>
                        )}
                        
                        {currentStep.transitionAwareness && (
                          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <Bell className="w-4 h-4 text-emerald-400" />
                              <span className="text-emerald-400 text-xs uppercase tracking-wider font-medium">Sensory Awareness</span>
                            </div>
                            <p className="text-emerald-200/80 text-sm">
                              {currentStep.transitionAwareness}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 text-emerald-300 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>About {currentStep.transitionDuration}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-white/50 text-xs pt-1">
                          <span>{currentStep.fromArea}</span>
                          <ArrowRight className="w-3 h-3" />
                          <span>{currentStep.toArea}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        {movementText?.primary && (
                          <p className="text-white font-medium">
                            {movementText.primary}
                          </p>
                        )}
                        
                        {movementText?.secondary && (
                          <p className={`text-sm ${currentStep?.isTransition ? 'text-amber-200/80' : 'text-white/70'}`}>
                            {movementText.secondary}
                          </p>
                        )}
                        
                        {currentStep?.isTransition && !currentStep?.isCustom && (
                          <div className="flex items-center gap-2 text-amber-300 text-sm">
                            <Clock className="w-4 h-4" />
                            <span>About {currentStep.transitionDuration}</span>
                          </div>
                        )}
                        
                        {!currentStep?.isTransition && (
                          <p className="text-emerald-400/80 text-xs italic">
                            You can pause here if needed.
                          </p>
                        )}
                        
                        {!currentStep?.isTransition && (currentStep?.categories || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {(currentStep?.categories || []).slice(0, 2).map((cat, i) => (
                              <span 
                                key={i}
                                className="px-2 py-1 rounded-lg text-xs font-medium"
                                style={{
                                  backgroundColor: `${getTrafficLightColor(cat.traffic_light)}20`,
                                  color: getTrafficLightColor(cat.traffic_light)
                                }}
                              >
                                {(cat.category || '').split(' ')[0]}: {getTrafficLightLabel(cat.traffic_light)}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="space-y-2 pt-2">
                      {exitOptions.length > 0 && !currentStep?.isTransition && (
                        <button className="w-full bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-xl transition-colors flex items-center gap-2">
                          <Coffee className="w-4 h-4" />
                          <span>Need a break?</span>
                          <ChevronRight className="w-4 h-4 ml-auto" />
                        </button>
                      )}
                      <button 
                        onClick={goToExploreHub}
                        className="w-full bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Exit Route
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Navigation Controls */}
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setCurrentStepIndex(Math.max(0, currentStepIndex - 1))}
                    disabled={currentStepIndex === 0}
                    className="flex-1 bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={() => setCurrentStepIndex(Math.min(route.length - 1, currentStepIndex + 1))}
                    disabled={currentStepIndex === route.length - 1}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-30 text-white py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default ExperienceHubCore;
